
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '@teammood/shared/ui/snack-bar';
import { User, UserService } from '@teammood/shared/util/user';
import { EMPTY, Subject, catchError, mergeMap, of, takeUntil, tap, throwError } from 'rxjs';
import { Team } from '../../team.type';
import { TeamMemberDialogService } from '../team-member-dialog.service';
import { TmTeamMemberFormComponent } from '../team-member-form/team-member-form.component';
import { TeamMemberService } from '../team-member.service';
import { TeamMember } from '../team-member.type';

@Component({
    selector: 'tm-team-member-form-dialog',
    templateUrl: 'team-member-form-dialog.component.html',
    styleUrls: ['team-member-form-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatDialogActions,
        MatDialogContent,
        TmTeamMemberFormComponent,
        TranslateModule
    ]
})
export class TeamMemberFormDialogComponent implements OnInit {

  @ViewChild(TmTeamMemberFormComponent)
  teamMemberForm: TmTeamMemberFormComponent;

  currentUser: User;

  onMemberChanged = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TeamMemberDialogData,
    public dialogRef: MatDialogRef<TeamMemberFormDialogComponent, TeamMember | boolean>,
    private _snackBarService: SnackBarService,
    private _teamMemberDialogService: TeamMemberDialogService,
    private _teamMemberService: TeamMemberService,
    private _translateService: TranslateService,
    private _userService: UserService,
  ) { }

  async ngOnInit() {
    this.currentUser = await this._userService.get();
  }

  close() {
    const updatedTeamMember = this.teamMemberForm.isUpdate && this.teamMemberForm.form.dirty ? this.teamMemberForm.form.getRawValue() : false;
    this.dialogRef.close(updatedTeamMember);
  }

  async delete() {
    const deleteDialogRef = await this._teamMemberDialogService.delete(this.data.member);

    deleteDialogRef.beforeClosed().pipe(
      mergeMap((action) => {
          switch(action) {
              case "delete":
                  return this._teamMemberService.delete(this.data.team.id, this.data.member.id)
              case "disable":
                  return this._teamMemberService.toggle(this.data.team.id, this.data.member.id)
              default:
                  return EMPTY;
          }
      }),
      tap(() => {
        this._snackBarService.success(this._translateService.instant('user_removed'));
        this.onMemberChanged.next(this.data.member);
        this.dialogRef.close(true);
      }),
      catchError((err) => {
        this._snackBarService.error(JSON.stringify(err));
        throw err;
      }),
      takeUntil(this.dialogRef.afterClosed()),
    ).subscribe()
  }

  async create() {
    const newTeamMember = this.teamMemberForm.form.getRawValue();

    this._teamMemberService.create(this.data.team.id, newTeamMember as TeamMember).pipe(
      catchError((err: HttpErrorResponse) => {
        const errorMessage = err.error && err.error.length > 0 && err.error[0].message;
        
        //If no explicit error message, let globalErrorHandler handle it
        if (!errorMessage) {
          return throwError(() => err);
        } else {
          this._snackBarService.error(errorMessage || 'error');
          
          return of(null);
        }

      }),
      tap((newTeamMember) => {
        if (newTeamMember) {
          this._snackBarService.success(this._translateService.instant('user_added'))
          this.dialogRef.close(newTeamMember);
        }
      }),
      takeUntil(this.dialogRef.beforeClosed()),
    ).subscribe()
  }

}

export interface TeamMemberDialogData {
  member: Partial<TeamMember>;
  team: Team;
  creation: boolean
}